import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import nacmeLogo from './assets/nacme_logo.jpg';
import tlaLogo from './assets/tla_logo.png';
import HelpDesk from './components/HelpDesk';
import SalesChat from './components/SalesChat';
import LandingPage from './LandingPage';
import PromoVideo from './PromoVideo';
import Demo from './Demo';
import App from './App';
import ApplicantRegistrationForm from './ApplicantRegistrationForm';
import RecruiterRegistrationForm from './RecruiterRegistrationForm';
import AdminApproveRecruiters from './AdminApproveRecruiters';
import AdminWrapper from './AdminWrapper';
import AdminRecruiterTokens from './AdminRecruiterTokens';
import AdminOrganizationMatches from './AdminOrganizationMatches';
import AdminStudentMatches from './AdminStudentMatches';
import AdminStudentMatchesRDL from './AdminStudentMatchesRDL';
import AdminNonMatchingStudents from './AdminNonMatchingStudents';
import AdminDuplicateStudents from './AdminDuplicateStudents';
import AdminMergeStudents from './AdminMergeStudents';
import AdminMultiCompanyStudents from './AdminMultiCompanyStudents';
import AdminCompanyManagement from './AdminCompanyManagement';
import AdminCompanyDashboardStats from './AdminCompanyDashboardStats';
import AdminWorkdayConfiguration from './AdminWorkdayConfiguration';
import AdminUserManagement from './AdminUserManagement';
import AdminBoardLiaisonManagement from './AdminBoardLiaisonManagement';
import AdminNetworkAnalytics from './AdminNetworkAnalytics';
import AdminContentModeration from './AdminContentModeration';
import AdminCurationTopics from './AdminCurationTopics';
import AdminPendingPosts from './AdminPendingPosts';
import AdminCuratorManagement from './AdminCuratorManagement';
import CompanyInterestSelection from './CompanyInterestSelection';
import AllCompanies from './AllCompanies';
import LoginForm from './LoginForm';
import PartnerVerificationForm from './PartnerVerificationForm';
import PartnerLogin from './PartnerLogin';
import PartnerDashboard from './PartnerDashboard';
import PartnerActivation from './PartnerActivation';
import PartnerForgotPassword from './PartnerForgotPassword';
import PartnerResetPassword from './PartnerResetPassword';
import ActivationTokensPage from './ActivationTokensPage';
import RecruiterLogin from './RecruiterLogin';
import RecruiterActivation from './RecruiterActivation';
import RecruiterDashboard from './RecruiterDashboard';
import RecruiterMapView from './RecruiterMapView';
import RecruiterJobPostings from './RecruiterJobPostings';
import RecruiterInteractionHistory from './RecruiterInteractionHistory';
import RecruiterResetPassword from './RecruiterResetPassword';
import RecruiterFAQ from './RecruiterFAQ';
import UpdateApplicationWrapper from './UpdateApplicationWrapper';
import BoardLiaisonLogin from './BoardLiaisonLogin';
import BoardLiaisonDashboard from './BoardLiaisonDashboard';
import StudentDashboard from './StudentDashboard';
import StudentDirectLogin from './StudentDirectLogin';
import StudentForgotPassword from './StudentForgotPassword';
import StudentResetPassword from './StudentResetPassword';
import AlumniLogin from './AlumniLogin';
import AlumniRegistration from './AlumniRegistration';
import AlumniUserAgreement from './AlumniUserAgreement';
import AlumniSmsTerms from './AlumniSmsTerms';
import SmsOptInScreenshot from './SmsOptInScreenshot';
import AlumniForgotPassword from './AlumniForgotPassword';
import AlumniResetPassword from './AlumniResetPassword';
import AlumniDashboard from './AlumniDashboard';
import AlumniJobBoard from './AlumniJobBoard';
import AlumniEmailVerification from './AlumniEmailVerification';
import AlumniDirectory from './AlumniDirectory';
import AlumniPublicProfile from './AlumniPublicProfile';
import AlumniMessages from './AlumniMessages';
import AlumniGroups from './AlumniGroups';
import AlumniProfileManagement from './AlumniProfileManagement';
import AlumniNewsCuration from './AlumniNewsCuration';
import ResumeReviewPage from './components/resume/ResumeReviewPage';
import JobAnalyticsDashboard from './JobAnalyticsDashboard';
import AdminBusinessDevelopment from './AdminBusinessDevelopment';
import AdminFeatureToggle from './AdminFeatureToggle';
import AdminPlatformSettings from './AdminPlatformSettings';
import AdminStudentRecords from './AdminStudentRecords';
import AdminStudentSurveyReport from './components/AdminStudentSurveyReport';
import AdminRecruiterStatsReport from './components/AdminRecruiterStatsReport';
import AdminRecruiterPipeline from './components/AdminRecruiterPipeline';
import AdminUniversityPartnerReport from './components/AdminUniversityPartnerReport';
import AdminExecutiveIntelligence from './components/AdminExecutiveIntelligence';
import AdminAlumniResearchAssignments from './components/AdminAlumniResearchAssignments';
import AdminMembershipManager from './components/admin/AdminMembershipManager';
import AdminCRM from './components/admin/AdminCRM';
import AdminOutreachManager from './components/AdminOutreachManager';
import AdminSWEE from './components/admin/AdminSWEE';
import AdminGrantManagement from './components/admin/AdminGrantManagement';
import AdminInternshipPostings from './components/AdminInternshipPostings';
import SWEEReportViewer from './components/admin/SWEEReportViewer';
import ExecProfileViewer from './components/ExecProfileViewer';
import AdminStudentDetail from './AdminStudentDetail';
import AdminResumeReviews from './AdminResumeReviews';
import AdminForgotPassword from './AdminForgotPassword';
import AdminResetPassword from './AdminResetPassword';
import OfferView from './OfferView';
import BlogList from './components/blog/BlogList';
import BlogPost from './components/blog/BlogPost';
import BlogAdmin from './components/blog/BlogAdmin';
import BlogEditor from './components/blog/BlogEditor';
import FilmScreening from './FilmScreening';
import ConsentResponse from './ConsentResponse';
import ProfessionalConduct from './ProfessionalConduct';
import BrokerageMeetingRoom from './components/video/BrokerageMeetingRoom';
import { getValidToken } from './utils/authUtils';
import ChatWrapper from './components/chat/ChatWrapper';
import { HMSRoomProvider } from '@100mslive/react-sdk';

// Platform detection - 'nacme' (default) or 'tla'
const PLATFORM = process.env.REACT_APP_PLATFORM || 'nacme';
const IS_TLA = PLATFORM === 'tla';

const AppRouter = () => {
  // Set document title based on platform
  useEffect(() => {
    document.title = IS_TLA ? 'TLA Engineer Network' : 'NACME Searchlight';
  }, []);

  // TLA Landing Page - redirects to login
  const TLALandingPage = () => (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white flex items-center justify-center">
      <div className="w-full max-w-md p-8 text-center">
        <img
          className="mx-auto h-48 sm:h-60 w-auto mb-6"
          src={tlaLogo}
          alt="TLA Logo"
        />
        <h1 className="text-3xl font-bold text-sky-800 mb-4">TLA Engineer Network</h1>
        <p className="text-gray-600 mb-8">Technical Leadership Association <span className="whitespace-nowrap">Alumni Platform</span></p>
        <div className="space-y-4">
          <a
            href="/login"
            className="block w-full py-3 px-4 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition"
          >
            Sign In
          </a>
          <a
            href="/register"
            className="block w-full py-3 px-4 border border-sky-600 text-sky-600 rounded-lg hover:bg-sky-50 transition"
          >
            Create Account
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <HMSRoomProvider>
      <Router>
        {/* NACME-only components */}
        {!IS_TLA && <HelpDesk />}
        {!IS_TLA && (
          <SalesChat
            allowedPaths={[
              '/',                    // Landing page
            ]}
            labelText="Recruit with us!"
          />
        )}
      {/* Chat Widget - shows on dashboard pages for authenticated users */}
      <ChatWrapper />
      <Routes>
        {/* ============================================ */}
        {/* TLA PLATFORM ROUTES (app.tla.engineer)      */}
        {/* ============================================ */}
        {IS_TLA && (
          <>
            {/* TLA Landing */}
            <Route path="/" element={<TLALandingPage />} />

            {/* TLA Auth routes (at root, not /alumni/) */}
            <Route path="/login" element={<AlumniLogin />} />
            <Route path="/register" element={<AlumniRegistration />} />
            <Route path="/user-agreement" element={<AlumniUserAgreement />} />
            <Route path="/sms-terms" element={<AlumniSmsTerms />} />
            <Route path="/sms-opt-in" element={<SmsOptInScreenshot />} />
            <Route path="/forgot-password" element={<AlumniForgotPassword />} />
            <Route path="/reset-password" element={<AlumniResetPassword />} />
            <Route path="/verify-email/:token" element={<AlumniEmailVerification />} />

            {/* TLA Main routes (at root, not /alumni/) */}
            <Route path="/dashboard" element={<AlumniDashboard />} />
            <Route path="/job-board" element={<AlumniJobBoard />} />
            <Route path="/directory" element={<AlumniDirectory />} />
            <Route path="/profile/:alumniId" element={<AlumniPublicProfile />} />
            <Route path="/messages" element={<AlumniMessages />} />
            <Route path="/groups" element={<AlumniGroups />} />
            <Route path="/profile-management" element={<AlumniProfileManagement />} />
            <Route path="/news-curation" element={<AlumniNewsCuration />} />
            <Route path="/resume-review" element={<ResumeReviewPage />} />

            {/* TLA catch-all - redirect to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}

        {/* ============================================ */}
        {/* NACME PLATFORM ROUTES (nacme.app)           */}
        {/* ============================================ */}
        {!IS_TLA && (
          <>
            {/* Landing page as the root route */}
            <Route path="/" element={<LandingPage />} />

        {/* Brokerage Meeting Room - No auth required, access gated by session UUID */}
        <Route path="/video/meeting/:sessionId" element={<BrokerageMeetingRoom />} />

        {/* Promotional Video Page */}
        <Route path="/promo-video" element={<PromoVideo />} />

        {/* Demo Page */}
        <Route path="/demo" element={<Demo />} />

        {/* Main application route - Admin only */}
        <Route 
          path="/app/*" 
          element={
            getValidToken() ? (
              <App />
            ) : (
              <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
                <div className="w-full max-w-md p-8">
                  <div className="text-center mb-8">
                    <img
                      src={nacmeLogo}
                      alt="NACME Logo"
                      className="h-16 w-auto mx-auto mb-4 drop-shadow"
                    />
                    <h1 className="text-2xl font-bold text-gray-900">
                      Admin Access Required
                    </h1>
                    <p className="mt-2 text-sm text-gray-600">
                      Please log in with admin credentials to access the main application
                    </p>
                  </div>
                  <LoginForm onLoginSuccess={() => window.location.reload()} />
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => window.location.href = '/'}
                      className="text-sm text-gray-500 hover:text-gray-700 underline"
                    >
                      Back to Home
                    </button>
                  </div>
                </div>
              </div>
            )
          } 
        />
        
        {/* Registration routes */}
        <Route path="/register" element={<ApplicantRegistrationForm />} />
        <Route path="/company-interests" element={<CompanyInterestSelection />} />
        <Route path="/update-application" element={<UpdateApplicationWrapper />} />
        <Route path="/application-form" element={<ApplicantRegistrationForm />} />
        <Route path="/recruiter-signup" element={<RecruiterRegistrationForm />} />
        
        {/* All Companies route */}
        <Route path="/companies" element={<AllCompanies />} />
        
        {/* Partner routes */}
        <Route path="/partner/verify" element={<PartnerVerificationForm />} />
        <Route path="/partner/login" element={<PartnerLogin />} />
        <Route path="/partner/forgot-password" element={<PartnerForgotPassword />} />
        <Route path="/partner/reset-password" element={<PartnerResetPassword />} />
        <Route path="/partner/dashboard" element={<PartnerDashboard />} />
        <Route path="/partner/activate" element={<PartnerActivation />} />
        
        {/* Recruiter routes */}
        <Route path="/recruiter/login" element={<RecruiterLogin />} />
        <Route path="/recruiter/activate" element={<RecruiterActivation />} />
        <Route path="/recruiter/reset-password" element={<RecruiterResetPassword />} />
        <Route path="/recruiter/dashboard" element={<RecruiterDashboard />} />
        <Route path="/recruiter/dashboard/map" element={<RecruiterMapView />} />
        <Route path="/recruiter/faq" element={<RecruiterFAQ />} />
        <Route path="/recruiter/job-postings" element={<RecruiterJobPostings />} />
        <Route path="/recruiter/interactions/:recruiterId" element={<RecruiterInteractionHistory />} />
        
        {/* Board Liaison routes */}
        <Route path="/board-liaison/login" element={<BoardLiaisonLogin />} />
        <Route path="/board-liaison/dashboard" element={<BoardLiaisonDashboard />} />
        
        {/* Alumni routes */}
        <Route path="/alumni/login" element={<AlumniLogin />} />
        <Route path="/alumni/register" element={<AlumniRegistration />} />
        <Route path="/alumni/user-agreement" element={<AlumniUserAgreement />} />
        <Route path="/alumni/sms-terms" element={<AlumniSmsTerms />} />
        <Route path="/alumni/sms-opt-in" element={<SmsOptInScreenshot />} />
        <Route path="/alumni/forgot-password" element={<AlumniForgotPassword />} />
        <Route path="/alumni/reset-password" element={<AlumniResetPassword />} />
        <Route path="/alumni/verify-email/:token" element={<AlumniEmailVerification />} />
        <Route path="/alumni/dashboard" element={<AlumniDashboard />} />
        <Route path="/alumni/job-board" element={<AlumniJobBoard />} />
        <Route path="/alumni/directory" element={<AlumniDirectory />} />
        <Route path="/alumni/profile/:alumniId" element={<AlumniPublicProfile />} />
        <Route path="/alumni/messages" element={<AlumniMessages />} />
        <Route path="/alumni/groups" element={<AlumniGroups />} />
        <Route path="/alumni/profile-management" element={<AlumniProfileManagement />} />
        <Route path="/alumni/news-curation" element={<AlumniNewsCuration />} />
        <Route path="/alumni/resume-review" element={<ResumeReviewPage />} />

        {/* Film Screening route - Public with password protection */}
        <Route path="/screening/moses" element={<FilmScreening />} />

        {/* Consent response - Public, token-authenticated */}
        <Route path="/consent/:token" element={<ConsentResponse />} />

        {/* Professional conduct standards - Public */}
        <Route path="/professional-conduct" element={<ProfessionalConduct />} />

        {/* Student routes */}
        <Route path="/student/login" element={<StudentDirectLogin />} />
        <Route path="/student/forgot-password" element={<StudentForgotPassword />} />
        <Route path="/student/reset-password" element={<StudentResetPassword />} />
        <Route path="/student/dashboard" element={<StudentDashboard />} />
        
        {/* Offer view route - public for JWT token access */}
        <Route path="/offer/view" element={<OfferView />} />
        
        {/* Blog routes */}
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        
        {/* Blog Admin routes */}
        <Route path="/blog-admin" element={<BlogAdmin />} />
        <Route path="/blog-admin/new" element={<BlogEditor />} />
        <Route path="/blog-admin/edit/:postId" element={<BlogEditor />} />
        
        {/* Admin routes */}
        <Route path="/admin" element={<AdminWrapper />} />
        <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
        <Route path="/admin/reset-password" element={<AdminResetPassword />} />

        <Route
          path="/admin/job-analytics"
          element={
            getValidToken() ? (
              <JobAnalyticsDashboard />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/business-development"
          element={
            getValidToken() ? (
              <AdminBusinessDevelopment />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/feature-toggles"
          element={
            getValidToken() ? (
              <AdminFeatureToggle />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/platform-settings"
          element={
            getValidToken() ? (
              <AdminPlatformSettings />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/student-records"
          element={
            getValidToken() ? (
              <AdminStudentRecords />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/student-survey-report"
          element={
            getValidToken() ? (
              <AdminStudentSurveyReport />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/recruiter-stats-report"
          element={
            getValidToken() ? (
              <AdminRecruiterStatsReport />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/recruiter-pipeline"
          element={
            getValidToken() ? (
              <AdminRecruiterPipeline />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/university-partner-report"
          element={
            getValidToken() ? (
              <AdminUniversityPartnerReport />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/executive-intelligence"
          element={
            getValidToken() ? (
              <AdminExecutiveIntelligence />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/alumni-research-assignments"
          element={
            getValidToken() ? (
              <AdminAlumniResearchAssignments />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/membership-manager"
          element={
            getValidToken() ? (
              <AdminMembershipManager />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/crm"
          element={
            getValidToken() ? (
              <AdminCRM />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />
        <Route
          path="/admin/internship-postings"
          element={
            getValidToken() ? (
              <AdminInternshipPostings />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/outreach-manager"
          element={
            getValidToken() ? (
              <AdminOutreachManager />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/grants"
          element={
            getValidToken() ? (
              <AdminGrantManagement />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/swee"
          element={
            getValidToken() ? (
              <AdminSWEE />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/swee/report/:reportId"
          element={
            getValidToken() ? (
              <SWEEReportViewer />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/executive-intelligence/profile/:profileId"
          element={
            getValidToken() ? (
              <ExecProfileViewer />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/students/:studentId"
          element={
            getValidToken() ? (
              <AdminStudentDetail />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/duplicate-students"
          element={
            getValidToken() ? (
              <AdminDuplicateStudents />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/merge-students/:studentId1/:studentId2"
          element={
            getValidToken() ? (
              <AdminMergeStudents />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/organization-matches" 
          element={
            getValidToken() ? (
              <AdminOrganizationMatches />
            ) : (
              <Navigate to="/admin" replace />
            )
          } 
        />
        
        <Route
          path="/admin/student-matches"
          element={
            getValidToken() ? (
              <AdminStudentMatches />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/student-matches-rdl"
          element={
            getValidToken() ? (
              <AdminStudentMatchesRDL />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/non-matching-students" 
          element={
            getValidToken() ? (
              <AdminNonMatchingStudents />
            ) : (
              <Navigate to="/admin" replace />
            )
          } 
        />
        
        <Route 
          path="/admin/multi-company-students" 
          element={
            getValidToken() ? (
              <AdminMultiCompanyStudents />
            ) : (
              <Navigate to="/admin" replace />
            )
          } 
        />
        
        <Route 
          path="/admin/activation-tokens" 
          element={
            getValidToken() ? (
              <ActivationTokensPage />
            ) : (
              <Navigate to="/admin" replace />
            )
          } 
        />
        
        <Route 
          path="/admin/recruiter-tokens" 
          element={
            getValidToken() ? (
              <AdminRecruiterTokens />
            ) : (
              <Navigate to="/admin" replace />
            )
          } 
        />
        
        <Route
          path="/admin/company-management"
          element={
            getValidToken() ? (
              <AdminCompanyManagement />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/company-stats"
          element={
            getValidToken() ? (
              <AdminCompanyDashboardStats />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/workday-configuration" 
          element={
            getValidToken() ? (
              <AdminWorkdayConfiguration />
            ) : (
              <Navigate to="/admin" replace />
            )
          } 
        />
        
        <Route
          path="/admin/user-management"
          element={
            getValidToken() ? (
              <AdminUserManagement />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/board-liaisons"
          element={
            getValidToken() ? (
              <AdminBoardLiaisonManagement />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/network-analytics" 
          element={
            getValidToken() ? (
              <AdminNetworkAnalytics />
            ) : (
              <Navigate to="/admin" replace />
            )
          } 
        />
        
        <Route
          path="/admin/content-moderation"
          element={
            getValidToken() ? (
              <AdminContentModeration />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/curation-topics"
          element={
            getValidToken() ? (
              <AdminCurationTopics />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/pending-posts"
          element={
            getValidToken() ? (
              <AdminPendingPosts />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/curator-management"
          element={
            getValidToken() ? (
              <AdminCuratorManagement />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        <Route
          path="/admin/resume-reviews"
          element={
            getValidToken() ? (
              <AdminResumeReviews />
            ) : (
              <Navigate to="/admin" replace />
            )
          }
        />

        {/* Catch all route - redirect to landing page */}
        <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
      </Router>
    </HMSRoomProvider>
  );
};

export default AppRouter;